/**
 * usePushNotifications - Hook for managing push notifications on native apps
 * 
 * This hook handles:
 * - Initializing push notification listeners
 * - Requesting permission at appropriate times
 * - Handling notification taps and navigation
 * 
 * PLATFORM SUPPORT:
 * - Android: Full support via @capacitor/push-notifications + Firebase FCM
 * - iOS: Full support via @capacitor/push-notifications + APNs
 * - Web: Not supported (isPushSupported() returns false)
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { 
  initPushNotifications, 
  requestPushPermission, 
  getPushPermissionStatus,
  isPushSupported,
  removePushListeners
} from '../services/pushNotifications';
import type { PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';

interface UsePushNotificationsOptions {
  /** Whether user is logged in */
  isLoggedIn: boolean;
  /** Profile completion percentage (0-100) */
  profileCompletion?: number;
  /** Callback when notification received in foreground */
  onNotificationReceived?: (notification: PushNotificationSchema) => void;
  /** Callback when user taps notification - receives action string */
  onNotificationAction?: (action: string) => void;
}

interface UsePushNotificationsResult {
  /** Whether push is supported on current platform */
  isSupported: boolean;
  /** Current permission status */
  permissionStatus: 'granted' | 'denied' | 'prompt' | 'unknown';
  /** Whether push is enabled for this user */
  isEnabled: boolean;
  /** Request push permission - call after user action */
  requestPermission: () => Promise<boolean>;
  /** Whether we're currently requesting permission */
  isRequesting: boolean;
  /** Whether the UI should show a push opt-in prompt (profile ≥ 50%, permission not yet asked) */
  shouldPrompt: boolean;
}

/**
 * Hook for managing push notifications in native apps
 * 
 * Usage:
 * ```tsx
 * const { isSupported, requestPermission, isEnabled } = usePushNotifications({
 *   isLoggedIn: true,
 *   profileCompletion: 80,
 *   onNotificationAction: (action) => {
 *     if (action === 'open_profile') setShowProfile(true);
 *   },
 * });
 * 
 * // Show prompt after profile is complete
 * if (isSupported && !isEnabled && profileCompletion >= 50) {
 *   // Show UI asking user to enable notifications
 * }
 * ```
 */
export function usePushNotifications({
  isLoggedIn,
  profileCompletion = 0,
  onNotificationReceived,
  onNotificationAction,
}: UsePushNotificationsOptions): UsePushNotificationsResult {
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown');
  const [isEnabled, setIsEnabled] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const initialized = useRef(false);
  const onNotificationReceivedRef = useRef(onNotificationReceived);
  const onNotificationActionRef = useRef(onNotificationAction);

  // Keep refs in sync with latest callbacks
  useEffect(() => {
    onNotificationReceivedRef.current = onNotificationReceived;
    onNotificationActionRef.current = onNotificationAction;
  }, [onNotificationReceived, onNotificationAction]);

  // Check if push is supported
  const isSupported = isPushSupported();

  // Handle notification action (user tapped notification)
  const handleNotificationAction = useCallback((action: ActionPerformed) => {
    console.log('📱 Notification tapped:', action);
    
    const data = action.notification.data;
    const actionType = data?.action as string;
    
    if (actionType && onNotificationActionRef.current) {
      onNotificationActionRef.current(actionType);
    }
  }, []);

  // Initialize push notifications when user logs in
  useEffect(() => {
    if (!isSupported || !isLoggedIn || initialized.current) {
      return;
    }

    const init = async () => {
      try {
        // Check current permission status
        const status = await getPushPermissionStatus();
        setPermissionStatus(status);
        setIsEnabled(status === 'granted');

        // Initialize listeners
        await initPushNotifications({
          onToken: (token) => {
            console.log('📱 FCM Token received:', token.substring(0, 20) + '...');
          },
          onNotification: (notification) => {
            console.log('📬 Notification received in foreground:', notification);
            onNotificationReceivedRef.current?.(notification);
          },
          onAction: handleNotificationAction,
        });

        initialized.current = true;
        console.log('📱 Push notifications initialized');
      } catch (error) {
        console.error('Failed to initialize push notifications:', error);
      }
    };

    init();

    // Cleanup on unmount
    return () => {
      if (initialized.current) {
        removePushListeners();
        initialized.current = false;
      }
    };
  }, [isSupported, isLoggedIn, handleNotificationAction]);

  // Request permission function
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      console.log('Push notifications not supported on this platform');
      return false;
    }

    setIsRequesting(true);
    try {
      const granted = await requestPushPermission();
      setIsEnabled(granted);
      setPermissionStatus(granted ? 'granted' : 'denied');
      console.log('📱 Push permission result:', granted);
      return granted;
    } catch (error) {
      console.error('Failed to request push permission:', error);
      return false;
    } finally {
      setIsRequesting(false);
    }
  }, [isSupported]);

  // Determine if we should show the opt-in prompt
  const shouldPrompt = isSupported && !isEnabled && permissionStatus === 'prompt' && profileCompletion >= 50;

  return {
    isSupported,
    permissionStatus,
    isEnabled,
    requestPermission,
    isRequesting,
    shouldPrompt,
  };
}
